/**
 * SINGLE SOURCE OF TRUTH for every offer's price, price id, and checkout URL,
 * plus the canonical site routes CTAs point at.
 *
 * Change a price or a destination HERE and it propagates automatically to:
 *   - `src/content/primary-cta.ts` (hero / mid-page / final band / sticky bar)
 *   - `src/content/pricing.ts`     (the pricing ladder)
 *   - `src/content/faq.ts`         (pricing answers + FAQPage JSON-LD)
 *   - `src/lib/masterclass-analytics.ts` (funnel value + price_id)
 *   - page copy and meta descriptions that interpolate `priceLabel`
 *
 * Rule: never hard-code a dollar amount, a `price=` query string, or a
 * `/book?...` URL in a component. Import from here instead.
 */

/** Canonical internal routes. Use these instead of string literals. */
export const ROUTES = {
  home: "/",
  scan: "/scan",
  book: "/book",
  masterclass: "/masterclass",
  invest: "/invest",
  inquire: "/inquire",
  advocacy: "/advocacy",
  strategySession: "/strategy-session",
} as const;

export type OfferId =
  | "session"
  | "masterclass"
  | "circle"
  | "keynote_deposit"
  | "coaching"
  | "enterprise";

export type Offer = {
  id: OfferId;
  /** Stripe price identifier used by the `?price=` param and analytics. */
  priceId: string | null;
  name: string;
  /** Numeric amount in USD. `null` for "from"/custom-scope offers. */
  priceUsd: number | null;
  /** Rendered price, e.g. "$297" or "From $3,500". */
  priceLabel: string;
  /** Billing cadence shown under the price, e.g. "/month", "one-time". */
  cadence: string;
  /** Where the buy/apply CTA goes. */
  href: string;
  /** Recurring offers require an account before checkout. */
  requiresAccount?: boolean;
};

const usd = (amount: number) => `$${amount.toLocaleString("en-US")}`;

/**
 * Stan store product links, keyed by price id. When a link is set here, every
 * buy button for that offer opens Stan instead of the on-site checkout.
 * Leave empty ("") to keep the on-site checkout.
 */
export const STAN_LINKS: Record<string, string> = {
  built_on_purpose_masterclass_onetime: "",
  book_a_session_onetime: "",
};

/** Buy URL for a price id: the Stan product if configured, else on-site checkout. */
export const checkoutHref = (priceId: string) =>
  STAN_LINKS[priceId] || `${ROUTES.book}?price=${priceId}`;

const offer = (
  o: Omit<Offer, "priceLabel" | "href"> & {
    priceLabel?: string;
    href?: string;
  },
): Offer => ({
  ...o,
  priceLabel: o.priceLabel ?? (o.priceUsd !== null ? usd(o.priceUsd) : ""),
  href: o.href ?? (o.priceId ? checkoutHref(o.priceId) : ROUTES.inquire),
});

export const OFFERS: Record<OfferId, Offer> = {
  session: offer({
    id: "session",
    priceId: "book_a_session_onetime",
    name: "Book a Session",
    priceUsd: 500,
    cadence: "one-time",
  }),
  masterclass: offer({
    id: "masterclass",
    priceId: "built_on_purpose_masterclass_onetime",
    name: "Built On Purpose Masterclass",
    priceUsd: 297,
    cadence: "one-time",
  }),
  circle: offer({
    id: "circle",
    priceId: "makechange_circle_monthly",
    name: "MAKECHANGE Circle",
    priceUsd: 47,
    cadence: "/month",
    requiresAccount: true,
  }),
  keynote_deposit: offer({
    id: "keynote_deposit",
    priceId: "keynote_deposit_onetime",
    name: "Keynote Deposit",
    priceUsd: 2500,
    cadence: "one-time",
  }),
  coaching: offer({
    id: "coaching",
    priceId: null,
    name: "1:1 Executive Coaching",
    priceUsd: 3500,
    priceLabel: "From $3,500",
    cadence: "/month · 6-month minimum",
    href: `${ROUTES.inquire}?lane=coaching`,
  }),
  enterprise: offer({
    id: "enterprise",
    priceId: null,
    name: "Org Transformation + Keynote",
    priceUsd: null,
    priceLabel: "From $25K",
    cadence: "custom scope · keynote from $15K",
    href: `${ROUTES.inquire}?lane=consulting`,
  }),
};

/** Look up an offer by its Stripe price id (used by /book deep links). */
export const offerByPriceId = (priceId: string): Offer | undefined =>
  Object.values(OFFERS).find((o) => o.priceId === priceId);

/** One-line pricing ladder summary, reused in copy and meta descriptions. */
export const PRICING_LADDER_SUMMARY = [
  `${OFFERS.circle.priceLabel}/mo Circle`,
  `${OFFERS.masterclass.priceLabel} masterclass`,
  `executive coaching from ${OFFERS.coaching.priceLabel.replace("From ", "")}/mo`,
  `org transformation from ${OFFERS.enterprise.priceLabel.replace("From ", "")}`,
].join(", ");
